/* =============================================================================
   CCC WISHLIST — COFFEE MODULE for /apps/iwish.

   phaser: skip — DO NOT cherry-pick this file into Phaser FPV Horizon. Every
   line of it is Central Coast Coffee business logic: roast days, pre-order
   ("Upcoming Roast") variants, the Coffee/Gear split, and the PLACEHOLDER
   variant rule.

   ADDITIVE BY CONSTRUCTION. Delete this file (and its script tag) and the
   wishlist page still works — it just loses the Next roast stat, the roast
   line, the "Roast to order" badge, the Coffee/Gear/Roast chips, the coffee
   collection links in the empty state, and the coffee wording. Nothing in the
   shared module knows this file exists.

   THE "+" TRAP: this is a theme asset, so "+" is safe here. It is NEVER safe
   in the app admin's template or JS boxes, which decode it as a space.

   THE ONE RULE WORTH READING BEFORE YOU EDIT THE VARIANT FILTER
   The roast rule is expressed PER OPTION VALUE, not per variant. That is not a
   detail — the Liquid picker it ports from only ever removes entries from the
   Roast Date option's own value list, and a value is "unavailable" there only
   when NO variant carrying it is buyable. Test the individual variant instead
   and you silently delete sold-out SIZES of a perfectly live roast date, which
   makes coffee cards disagree with both the PDP and the gear cards. See the
   long comment on filterVariants below.
   ========================================================================== */
(function () {
  'use strict';

  if (typeof window === 'undefined' || !window.document) { return; }

  /* Re-entry guard. The shared module de-dupes addStatCell / addMastheadLine /
     addFilter by id, but addBadges and filterVariants are plain pushes with no
     duplicate check — so a second copy of this script tag (the classic Phaser
     port mistake: copying the layout block into a section) would register the
     roast badge twice and eat two of the shared three-badge slots. Guard here
     rather than hoping the shared side grows an id contract. */
  if (window.__cccWishlistCoffeeLoaded) { return; }
  window.__cccWishlistCoffeeLoaded = true;

  /* Registration preamble. The shared file loads first (two defer'd external
     scripts execute in document order), so CCWishlist.extend normally already
     exists. We still queue for the reverse order, because "defer preserves
     document order" is one editor mistake away from false. */
  function reg(fn) {
    var NS = window.CCWishlist;
    if (NS && typeof NS.extend === 'function') { NS.extend(fn); return; }
    NS = window.CCWishlist = window.CCWishlist || {};
    (NS.q = NS.q || []).push(fn);
  }

  reg(function (api) {
    var u = api.util;

    /* Written by assets/custom-roast-manager.js. Shape:
       { roastISO: 'YYYY-MM-DD', formatted: 'Tue 22 Jul', beforeCutoff: bool } */
    var K_ROAST = 'ccc:roast-date';

    /* The cutoff and the roast day are Sydney-local. Comparing against the
       browser's local date would roll the roast over a day early for anyone
       travelling, and a stale "next roast" is worse than none. */
    function sydneyToday() {
      try { return new window.Intl.DateTimeFormat('en-CA', { timeZone: 'Australia/Sydney' }).format(new Date()); }
      catch (e) { return new Date().toISOString().slice(0, 10); }
    }

    function roastData() {
      var d = u.store.get(K_ROAST, null);
      if (!d || !d.roastISO) { return null; }
      if (d.roastISO < sydneyToday()) { return null; }   /* stale — say nothing */
      return d;
    }

    function isCoffee(card) { return /coffee beans/i.test(u.attr(card, 'data-tags')); }

    /* The variant string to judge a card by. The shared module MAY stash the
       live selection on the card when the shopper uses the size switcher
       (card.__ccwLiveVariant); if it does, that is the truth and the rendered
       .ccw-card__variant node may still be showing the saved title. Prefer the
       stash, fall back to the documented accessor. Written defensively so this
       file is correct whether or not the shared side carries that stash. */
    function liveVariantTitle(card) {
      var live = card && card.__ccwLiveVariant;
      if (live && live.title) { return String(live.title); }
      return u.variantTitle(card);
    }

    /* Pre-order. Two signals: the app's own form class, and the variant title
       naming an Upcoming Roast. Kept here rather than in the shared module —
       once the roast badge and the roast chip moved out, nothing generic
       consumed it. NOTE for the Phaser port: the product-preorder branch may
       well be generic app behaviour we have never proven dead. Treat that half
       as 'phaser: unknown' rather than asserting Phaser has no pre-orders. */
    function isPreorder(card) {
      var f = u.qs('form', card);
      if (f && f.className && f.className.indexOf('product-preorder') !== -1) { return true; }
      return /upcoming roast/i.test(liveVariantTitle(card));
    }

    /* ------------------------------------------------------ the roast stat */
    api.addStatCell({
      id: 'roast',
      label: 'Next roast',
      order: 30,                     /* Items = 10, Value = 20 */
      value: function () {
        var d = roastData();
        return d ? (d.formatted || d.roastISO) : null;   /* null hides the cell */
      }
    });

    /* ------------------------------------------------- the roast line */
    api.addMastheadLine({
      id: 'roast',
      order: 30,
      render: function (ctx) {
        var d = roastData();
        if (!d) { return null; }
        var n = ctx.cards.filter(isPreorder).length;
        if (n > 1 && d.beforeCutoff) {
          return 'Order by Tuesday 5pm and ' + n + ' coffees on your list go on the next roast.';
        }
        if (n === 1 && d.beforeCutoff) {
          return 'Order by Tuesday 5pm and 1 coffee on your list goes on the next roast.';
        }
        if (n > 0) {
          return 'This week’s roast is locked in. ' + n + (n === 1 ? ' coffee' : ' coffees') +
                 ' on your list will be roasted on ' + (d.formatted || 'the next roast day') + '.';
        }
        return 'Next roast day is ' + (d.formatted || 'coming up') + '.';
      }
    });

    /* --------------------------------------------------------- the badge */
    /* Reuses the shared base .ccw-badge with no tone modifier, exactly as the
       old inline version did — so this needs no coffee CSS. It lands after
       "Road freight only" in the badge list, so under the shared three-badge
       cap it is the first to drop on a card that is also sold out, on sale and
       dangerous goods. */
    api.addBadges(function (card) {
      return isPreorder(card) ? [{ label: 'Roast to order' }] : [];
    });

    /* --------------------------------------------------------- the chips */
    /* Orders reproduce the old rail exactly: All, Coffee, Gear, In stock,
       Price drops, On sale, Back in stock, Roast to order. */
    api.addFilter({ id: 'coffee', label: 'Coffee', order: 10, test: isCoffee });
    api.addFilter({ id: 'gear', label: 'Gear', order: 20, test: function (c) { return !isCoffee(c); } });
    api.addFilter({ id: 'roast', label: 'Roast to order', order: 99, test: isPreorder });

    /* --------------------------------- PLACEHOLDER + dead roast dates */

    /* Locate the Roast Date option and return its 1-BASED position, or 0.

       Two traps, both silent:
       (a) product.options is a string[] on some API versions and an object[]
           on others, and BOTH shapes ship in the wild. Get this wrong and the
           position stays 0, the gate never fires, and nothing is hidden with
           no error at all.
       (b) for the object shape the entry carries its OWN position, which is
           the authority — the array index is only a stand-in for it. An
           options array that arrives out of position order would otherwise
           point us at the Size option, whose values contain no PLACEHOLDER, so
           again: nothing hidden, no warning.
       The name is whitespace-normalised before matching because 'Roast  Date'
       and a non-breaking space are both things merchants type. FIRST match
       wins — a second option that also matches is a data error, and taking the
       last one would quietly prefer it. */
    function roastOptionPosition(product) {
      var opts = (product && product.options) || [];
      for (var i = 0; i < opts.length; i++) {
        var o = opts[i];
        var name = (typeof o === 'string' ? o : (o && o.name)) || '';
        /* \s in JS does not include U+00A0 in older engines — list it. */
        name = String(name).replace(/[\s ]+/g, ' ').trim();
        if (/^roast(ing)? date$/i.test(name)) {
          if (typeof o === 'string') { return i + 1; }
          var pos = parseInt(o && o.position, 10);
          return (!isNaN(pos) && pos > 0) ? pos : (i + 1);
        }
      }
      return 0;
    }

    /* The variant's value for that option. /products/x.js gives us both
       v.options (an array, parallel to product.options) and v.option1..3; the
       array is the more reliable of the two, so it is tried first. The whole
       variant title is a LAST resort — matching on it is how "Upcoming 1kg" as
       a size name could rescue a dead roast date. */
    function roastValue(v, pos) {
      var raw = '';
      if (v && Object.prototype.toString.call(v.options) === '[object Array]' && v.options.length >= pos) {
        raw = v.options[pos - 1];
      }
      if (!raw && v) { raw = v['option' + pos]; }
      if (!raw && v) { raw = v.title; }
      return String(raw || '').replace(/[\s ]+/g, ' ').trim().toUpperCase();
    }

    /* A verbatim port of the rule in snippets/variant-main-picker.liquid
       (there are six inline copies of it there; there is no shared helper):

         hide EVERY roast-date value containing 'PLACEHOLDER', unconditionally;
         ALSO hide any out-of-stock roast-date value UNLESS it contains
         'Upcoming' — that one is the pre-order CTA and must always survive.

       Gated the same way: the product must be tagged 'Coffee Beans' AND the
       option must be named 'Roast Date' or 'Roasting Date'.

       PER OPTION VALUE, NOT PER VARIANT. In the Liquid, a roast-date VALUE is
       unavailable only when no variant carrying it is buyable; the Size option
       is never subject to the rule at all. So this builds a value -> anyAvailable
       map first, then drops variants by their value's verdict. A 1kg that is
       temporarily sold out on a live roast date therefore survives and renders
       as the shared module's disabled, struck-through button — which is what
       the PDP does and what every non-coffee card does.

       NO SAVED-VARIANT EXEMPTION. A wishlist entry saved weeks ago routinely
       points at a roast-date row that has since been recycled back to
       'PLACEHOLDER 1'; that is the exact case this rule was raised for, so
       exempting the saved variant would defeat it on the one card guaranteed
       to be rendered. The shared module decides what to do when the saved
       variant is gone — it is not this file's call.

       Two deliberate divergences from the Liquid, both recorded:
       (a) case-INSENSITIVE where Liquid's `contains` is not, so a variant
           titled "Placeholder 2" vanishes here but still renders on the PDP.
           Safer direction, but the two surfaces can legally disagree.
       (b) it matches the OPTION VALUE, not the whole variant title, except in
           the last-resort fallback above. */
    api.filterVariants(function (variants, product) {
      if (!product || Object.prototype.toString.call(variants) !== '[object Array]') { return variants; }

      var tags = product.tags;
      if (Object.prototype.toString.call(tags) !== '[object Array]') {
        tags = typeof tags === 'string' ? tags.split(',') : [];
      }
      if (!/(^|,)\s*coffee beans\s*(,|$)/i.test(tags.join(','))) { return variants; }

      var pos = roastOptionPosition(product);
      if (!pos) {
        u.warn('coffee: Coffee Beans product with no roast-date option — ' + (product.handle || '?'));
        return variants;
      }

      /* Pass one: the option value's own availability, aggregated across every
         variant that carries it. This is the number the Liquid picker reads. */
      var anyAvailable = {};
      variants.forEach(function (v) {
        var value = roastValue(v, pos);
        if (!Object.prototype.hasOwnProperty.call(anyAvailable, value)) { anyAvailable[value] = false; }
        if (v && v.available) { anyAvailable[value] = true; }
      });

      /* Pass two: judge the VALUE, drop the variants that carry a dead one. */
      return variants.filter(function (v) {
        var value = roastValue(v, pos);
        if (value.indexOf('PLACEHOLDER') !== -1) { return false; }
        if (!anyAvailable[value] && value.indexOf('UPCOMING') === -1) { return false; }
        return true;
      });
    });

    /* ------------------------------------------------- empty-state links */
    /* Built in JS so the master template carries no CCC collection handles and
       a Phaser porter can paste it unchanged. Nothing is lost with JS off: the
       empty state ships hidden and is only ever revealed by the shared JS. */
    api.addEmptyLink({ href: '/collections/roasted-coffee-beans', label: 'Roasted coffee beans', order: 10 });
    api.addEmptyLink({ href: '/collections/coffee-grinders', label: 'Grinders', order: 20 });
    api.addEmptyLink({ href: '/collections/coffee-tools-accessories', label: 'Tools & accessories', order: 30 });

    /* -------------------------------------------------------------- copy */
    /* Registered at parse, so on a correctly-booting shared module these land
       before the first render. Do NOT wrap this file in a DOMContentLoaded
       handler: the dispatch line renders once and marks itself permanent, so a
       late override would leave the generic noun on screen for that page view.
       (If the shared module ever boots synchronously at parse instead of on
       DOMContentLoaded, that is a shared-side defect — this file cannot fix it
       from here, and delaying registration would only make it worse.) */
    api.setCopy('backOne', 'coffee came back in stock');
    api.setCopy('backMany', 'coffees came back in stock');
    api.setCopy('accountBody', '{n} · {v} tracked here. Sign in and your list follows you — phone, laptop, and the next time you clear your browser. We will flag price drops, restocks and roast days on the coffees you have saved.');
  });
}());
